import React from "react";
import type { BaseToggleGroupItem, ToggleGroupComponentProps } from "./types";

export const useToggleGroup = <Item extends BaseToggleGroupItem>({
  items,
  ...props
}: ToggleGroupComponentProps<Item>) => {
  const selectedFromProps =
    props.type === "single"
      ? [props.value ?? props.defaultValue].flatMap((item) =>
          item == null ? [] : [item]
        )
      : props.value ?? props.defaultValue ?? [];
  const [selectedIds, setSelectedIds] = React.useState(
    new Set<string>(selectedFromProps)
  );

  React.useEffect(() => {
    setSelectedIds(new Set(selectedFromProps));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFromProps.join("//"), setSelectedIds]);

  const isSelected = React.useCallback(
    (item: Item) => selectedIds.has(item.id),
    [selectedIds]
  );
  return {
    isSelected,
    props:
      props.type === "single"
        ? {
            type: "single" as const,
            value: props.value,
            defaultValue: props.defaultValue,
            onValueChange: (value: string) => {
              const selected = items.find((item) => item.id === value);
              if (selected != null) {
                setSelectedIds(new Set([selected.id]));
                props.onValueChange?.(selected);
              }
            },
          }
        : {
            type: "multiple" as const,
            value: props.value,
            defaultValue: props.defaultValue,
            onValueChange: (value: string[]) => {
              const selected = items.filter((item) => value.includes(item.id));
              setSelectedIds(new Set(selected.map((item) => item.id)));
              props.onValueChange?.(selected);
            },
          },
  };
};
